/**
 * What a song is CALLED on the board — pure, no React, no Firestore.
 *
 * THE DEFECT THIS EXISTS TO FIX (Anthony's review, 2026-09-02). A song was
 * saved under the teacher's raw typed query while the resolved title sat
 * unused in `metadata.title` right beside it, so the tab row read
 * `selfless cold composed` and `beabadoobee glue song`. Measured against prod
 * that day: 11 of 20 repertoire rows.
 *
 * NEITHER OBVIOUS FIX IS RIGHT, WHICH IS THE WHOLE REASON THIS IS A MODULE
 * WITH TESTS RATHER THAN A `.toUpperCase()` AT A CALL SITE:
 *
 *  - Title-casing the query gives `Beabadoobee Glue Song` — the artist is
 *    still welded to the title, *beabadoobee* is a deliberately lowercase
 *    stage name we would be "correcting", and it can never recover
 *    `Selfless, Cold and Composed` because the comma and the "and" are not in
 *    what the teacher typed.
 *  - Adopting `metadata.title` unconditionally fixes 8 of the 11 and
 *    CORRUPTS 2: search resolved "dreams fleetwood" to the album
 *    *Fleetwood Mac Live* and "Death Cup mom jeans" to the album *Best Buds*.
 *    A song silently renaming itself to an album reads as data corruption,
 *    which is worse than a lowercase title — the teacher's own words are at
 *    least right.
 *
 * So: adopt the resolved title only when it PLAUSIBLY DESCRIBES the same
 * song, and otherwise keep the teacher's words and merely tidy their casing.
 * Anthony chose this over "show both" and "make me confirm" on 2026-09-02,
 * because the one screen this lives on is the one designed to cost a teacher
 * no taps mid-lesson.
 */

/** Lowercase, punctuation → spaces, collapsed. "Selfless, Cold and Composed"
 *  → "selfless cold and composed". Matches RepertoireService's own
 *  `songNameLower` spirit without importing it (this file stays pure). */
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const tokens = (s) => norm(s).split(' ').filter(Boolean);

/**
 * Words that stay lowercase inside a title (never first or last).
 * Deliberately short: an aggressive list starts "correcting" real titles.
 */
const SMALL_WORDS = new Set([
	'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into',
	'nor', 'of', 'on', 'or', 'the', 'to', 'vs', 'with',
]);

/**
 * Conventional title case, with one important exception: a word that already
 * carries an uppercase letter PAST ITS FIRST CHARACTER is left exactly alone.
 * That preserves `ABBA`, `McDaniel`, `iPhone` and `R&B` — anything the teacher
 * typed with deliberate internal capitals is more likely right than our rule.
 */
export const titleCaseSongName = (raw) => {
	const text = String(raw || '').trim();
	if (!text) return '';
	const words = text.split(/(\s+)/); // keep the whitespace runs
	const wordIdx = [];
	words.forEach((w, i) => {
		if (w.trim()) wordIdx.push(i);
	});
	const first = wordIdx[0];
	const last = wordIdx[wordIdx.length - 1];

	return words
		.map((w, i) => {
			if (!w.trim()) return w;
			// Deliberate internal capitals — leave untouched.
			if (/[A-Z]/.test(w.slice(1))) return w;
			const bare = w.toLowerCase();
			if (i !== first && i !== last && SMALL_WORDS.has(bare.replace(/[^a-z]/g, ''))) {
				return bare;
			}
			return bare.replace(/[a-z]/, (c) => c.toUpperCase());
		})
		.join('');
};

/**
 * Levenshtein distance, capped — we only ever ask "is this within 1?", so the
 * loop bails as soon as it cannot be.
 */
const withinOneEdit = (a, b) => {
	if (a === b) return true;
	if (Math.abs(a.length - b.length) > 1) return false;
	// Walk both, allowing exactly one insert / delete / substitute.
	let i = 0;
	let j = 0;
	let edits = 0;
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			i += 1;
			j += 1;
			continue;
		}
		edits += 1;
		if (edits > 1) return false;
		if (a.length > b.length) i += 1;
		else if (b.length > a.length) j += 1;
		else {
			i += 1;
			j += 1;
		}
	}
	return edits + (a.length - i) + (b.length - j) <= 1;
};

/**
 * Is `word` present in `set`, allowing ONE typo?
 *
 * Added 2026-09-02 after the backfill dry run refused a row it should have
 * taken: a student's board carried "Superboy and the invisble girl" against a
 * resolved title of "Superboy and the Invisible Girl". Every token matched but
 * one, and that one was a single dropped letter — exactly the case where the
 * resolved title is BETTER than what was typed, and the strict matcher was
 * preserving a typo on a real student's screen.
 *
 * Deliberately narrow (length >= 5, distance 1): it can only ever rescue a
 * misspelling of a word that is otherwise there. It cannot admit the album
 * mismatches this matcher exists to reject, because those differ by whole
 * tokens — "Fleetwood Mac Live" against "dreams fleetwood" is not a typo.
 */
const hasNear = (set, word) => {
	if (set.has(word)) return true;
	if (word.length < 5) return false;
	for (const candidate of set) {
		if (candidate.length >= 5 && withinOneEdit(word, candidate)) return true;
	}
	return false;
};

/**
 * Does `title` plausibly name the same song the teacher searched for?
 *
 * TWO CONDITIONS, BOTH LOAD-BEARING, and the worked cases are in the test file:
 *
 * 1. One token set contains the other. The teacher usually types either LESS
 *    than the full title ("goodbye yellow" → *Goodbye Yellow Brick Road*) or
 *    MORE, by tacking on the artist ("beabadoobee glue song" → *Glue Song*).
 *    Both directions are the same gesture and both must pass. Plain substring
 *    containment does NOT work here: "selfless cold composed" is not a
 *    substring of "selfless cold and composed".
 * 2. Exact match, or at least TWO shared tokens. Without this a one-token
 *    title subsets almost any query — "let it go frozen" would happily adopt a
 *    result titled *Go*.
 *
 * Together these accept all 8 of the good prod rows and reject both album
 * mismatches, which is the bar this function was written against.
 */
export const titleMatchesQuery = (queryText, title) => {
	const q = tokens(queryText);
	const t = tokens(title);
	if (!q.length || !t.length) return false;

	const qSet = new Set(q);
	const tSet = new Set(t);
	const shared = [...qSet].filter((w) => hasNear(tSet, w)).length;
	const equal = qSet.size === tSet.size && shared === qSet.size;

	const querySubsetOfTitle = [...qSet].every((w) => hasNear(tSet, w));
	const titleSubsetOfQuery = [...tSet].every((w) => hasNear(qSet, w));

	if (!querySubsetOfTitle && !titleSubsetOfQuery) return false;
	return equal || shared >= 2;
};

/**
 * The name a song should be saved and displayed under.
 *
 * @param {string} queryText  what the teacher typed
 * @param {object|null} metadata  the resolved match, if any
 * @returns {string}
 */
export const resolveSongDisplayName = (queryText, metadata = null) => {
	const typed = String(queryText || '').trim();
	const resolved = String(metadata?.title || '').trim();

	// Nothing resolved (manual add, or a search that found only an artist
	// record): the teacher's words are all we have, so tidy them and stop.
	if (!resolved) return titleCaseSongName(typed);
	if (!typed) return resolved;

	// The resolved title is already correctly cased by its source — never
	// title-case it, or "beabadoobee" and "iPhone" get mangled on the way past.
	if (titleMatchesQuery(typed, resolved)) return resolved;

	return titleCaseSongName(typed);
};
